import type { SortType } from 'rsuite';
import type { ListingPagination } from '../../hooks/api/hook-builders/listing-hook-builder';
import type {
  Media,
  MediaHydrated,
} from '../../lib/flotiq-client/types/api-response';
import type { FileWithId } from '../../lib/store/newMediaStore';
import type {
  DataGridCheckRowsFn,
  DataGridColumnProps,
  DataGridColumnResizeFn,
  DataGridGetRowLinkOptionsFn,
  DataGridSortColumnFn,
} from '../DataGrid/DataGrid';
import type { DataGridColumnOptionsProps } from '../DataGridControl/DataGridControl';
import type { TagClickFn, TagEditFn } from './definedColumns';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import { EMPTY_ARRAY, EMPTY_FUNCTION, EMPTY_OBJECT } from '../../lib/globals';
import {
  getTestProps,
  resizeColumn,
  setInitialGridOptions,
} from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import useMediaUpload from '../../hooks/api/useMediaUpload';
import useDebounceCallback from '../../hooks/useDebounceCallback';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import UserContext from '../../contexts/UserContext';

import DataGrid from '../DataGrid/DataGrid';
import Loader from '../Loader/Loader';
import StatusBar from '../StatusBar/StatusBar';
import { getDefinedColumns } from './definedColumns';

const OPTIONS_KEY = 'cms.media-library-grid-state';

interface MediaDataGridProps {
  /**
   * Media data
   */
  media?: MediaHydrated[];
  /**
   * If media are loading
   */
  isLoading?: boolean;
  /**
   * On upload callback
   */
  pagination: ListingPagination;
  /**
   * Current media limit on page
   */
  limit?: number;
  /**
   * On reload data callback
   */
  reloadMedia?: () => void;
  /**
   * Current order
   */
  order?: {
    order_by?: string;
    order_direction?: SortType;
  };
  /**
   * On order change callback
   */
  handleOrder?: (key: string, value?: string) => void;
  /**
   * Selected media
   */
  selectedMedia?: Record<string, Media | MediaHydrated>;
  /**
   * Callback for selecting media
   */
  selectMedia?: (media: Media | MediaHydrated) => void;
  /**
   * On tag edit callback
   */
  onTagEdit?: TagEditFn;
  /**
   * On tag click callback
   */
  onTagClick: TagClickFn;
  /**
   * On variant create callback
   */
  onVariantCreate: (media: MediaHydrated) => Promise<void>;
  /**
   *   Test id for user media subpage
   */
  testId?: string;
}

const MediaDataGrid = ({
  media = EMPTY_ARRAY,
  isLoading = false,
  pagination,
  limit = 12,
  reloadMedia = EMPTY_FUNCTION,
  order,
  handleOrder = EMPTY_FUNCTION,
  selectedMedia = EMPTY_OBJECT,
  selectMedia = EMPTY_FUNCTION,
  onTagEdit = EMPTY_FUNCTION,
  onTagClick = EMPTY_FUNCTION,
  onVariantCreate,
  testId = '',
}: MediaDataGridProps) => {
  const { t } = useTranslation();
  const { newMedia, removeNewMedia } = useMediaUpload();
  const { buildUrlWithSpace } = useSelectedSpace();
  const mediaGridContainer = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState<DataGridColumnProps[]>([]);

  const [gridOptions, setGridOptions, { removeItem: removeSavedGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(OPTIONS_KEY);

  const { permissions } = use(UserContext);

  const canUpdate = permissions.canCo(
    '_media',
    RolePermissions.PERMISSIONS_TYPES.UPDATE,
  );

  const handleInitialGridOptions = useCallback(
    (cols: DataGridColumnProps[], minWidth: number) => {
      setInitialGridOptions(
        cols,
        minWidth,
        mediaGridContainer.current
          ? mediaGridContainer.current.offsetWidth - 50
          : 0,
        setGridOptions,
        setColumns,
      );
    },
    [setGridOptions],
  );

  useEffect(() => {
    const cols: DataGridColumnProps[] = [];

    const gridOptionsByID =
      gridOptions?.reduce(
        (acc, elem) => {
          acc[elem.colId] = elem;
          return acc;
        },
        {} as Record<string, DataGridColumnOptionsProps>,
      ) || {};

    const definedColumns = getDefinedColumns(
      gridOptionsByID,
      onTagEdit,
      onTagClick,
      onVariantCreate,
      canUpdate,
      t,
      testId,
    );

    Object.keys(definedColumns).forEach((accessor) => {
      cols.push(definedColumns[accessor as keyof typeof definedColumns]);
    });

    if (!gridOptions) {
      handleInitialGridOptions(cols, 200);
    } else {
      setColumns(cols);
    }
  }, [
    t,
    handleInitialGridOptions,
    gridOptions,
    testId,
    onTagEdit,
    onTagClick,
    onVariantCreate,
    buildUrlWithSpace,
    canUpdate,
  ]);

  const handleColumnResize = useCallback<DataGridColumnResizeFn>(
    (columnWidth, dataKey) =>
      resizeColumn(columnWidth, dataKey, gridOptions, setGridOptions),
    [gridOptions, setGridOptions],
  );

  const onSortColumn = useCallback<DataGridSortColumnFn>(
    (field, order) => {
      handleOrder('order_by', field);
      handleOrder('order_direction', order);
    },
    [handleOrder],
  );

  const debouncedOnColumnResize = useDebounceCallback(handleColumnResize, 100);

  const handleResetGrid = useCallback(() => {
    removeSavedGridOptions();
    setGridOptions(undefined);
    removeNewMedia();
  }, [removeNewMedia, removeSavedGridOptions, setGridOptions]);

  const newMediaToShow = useMemo(
    () =>
      (newMedia || [])
        .filter(({ state }) => state === 'uploaded')
        .map(({ media }) => media),
    [newMedia],
  );

  const mediaDict = useMemo(
    () =>
      [...media, ...newMediaToShow].reduce(
        (acc, currentMedia) => {
          acc[currentMedia.id] = currentMedia;
          return acc;
        },
        {} as Record<
          string,
          Media | FileWithId | MediaHydrated | { id: string }
        >,
      ),
    [media, newMediaToShow],
  );

  const handleCheck = useCallback<DataGridCheckRowsFn>(
    (ids) => {
      const selectedIds = new Set(Object.keys(selectedMedia));
      ids.forEach((idToSelect) => {
        if (!selectedIds.has(`${idToSelect}`) && mediaDict[idToSelect])
          selectMedia(mediaDict[idToSelect] as Media | MediaHydrated);
      });
    },
    [mediaDict, selectMedia, selectedMedia],
  );

  const reloadData = useCallback(() => {
    reloadMedia();
    removeNewMedia();
  }, [reloadMedia, removeNewMedia]);

  const isNewMediaAdding = useMemo(
    () => newMedia.findIndex(({ state }) => state === 'uploading') > -1,
    [newMedia],
  );

  const getRowLinkOptions = useCallback<DataGridGetRowLinkOptionsFn>(
    (accessor, rowData) =>
      accessor === 'fileName'
        ? {
            to: getMediaUrl(rowData, 0, 0, false),
            target: '_blank',
            rel: 'noreferrer',
          }
        : { to: buildUrlWithSpace(`media/edit/${rowData.id}`) },
    [buildUrlWithSpace],
  );

  return (
    <div
      className="h-[calc(100vh-300px)] md:h-[calc(100vh-400px)]"
      ref={mediaGridContainer}
    >
      <DataGrid
        columns={columns}
        data={[...newMediaToShow, ...media]}
        isLoading={isNewMediaAdding || isLoading}
        loadingIcon={<Loader size={'small'} type={'spinner-grid'} />}
        onSortColumn={onSortColumn}
        sort={order?.order_by}
        sortOrder={order?.order_direction}
        statusBar={
          <StatusBar
            rows={pagination.count + newMediaToShow.length}
            handleDataUpdate={reloadData}
            resultsFrom={(pagination.current_page - 1) * limit + 1}
            resultsTo={
              (pagination.current_page - 1) * limit +
              pagination.count +
              newMediaToShow.length
            }
            resultsTotalCount={pagination.total_count + newMediaToShow.length}
            handleResetGrid={handleResetGrid}
            {...getTestProps(testId, 'statusbar', 'testId')}
          />
        }
        noDataInfo={
          <span className="flex items-center justify-center w-full h-full">
            {t('Users.FiltersEmptyResult')}
          </span>
        }
        additionalClasses="bg-white dark:bg-slate-900"
        rowHeight={70}
        onResize={debouncedOnColumnResize}
        autoHeight={false}
        checkedRows={Object.keys(selectedMedia)}
        onCheckRows={handleCheck}
        hasSelectColumn
        fillHeight
        getRowLinkOptions={getRowLinkOptions}
        {...getTestProps(testId, 'grid', 'testId')}
      />
    </div>
  );
};

export default MediaDataGrid;
